import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useUtilities from "../../data/useUtilities";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ContactType } from "../../models/Contact";
import { IVehicle } from "../../models/Vehicle";
import useNotification from "../notifications/useNotifications";
import LocationForm, { LocationFormAddress, LocationFormValues } from "../shared/Vehicle/LocationForm";
import VehicleCollectionQuestions from "./VehicleCollectionQuestions";

const emptyForm: LocationFormValues = {
  fullname: "",
  contactType: ContactType.Customer,
  telephone1: "",
  telephone2: "",
  email: "",
  line1: "",
  line2: null,
  line3: null,
  line4: null,
  locality: null,
  town: null,
  county: null,
  postcode: null,
};

function getFormValuesFromVehicle(v: IVehicle): LocationFormValues {
  return {
    fullname: "",
    contactType: ContactType.Customer,
    telephone1: "",
    telephone2: "",
    email: "",
    line1: v.line1 ? v.line1.trim() : "",
    line2: v.line2,
    line3: v.line3,
    line4: v.line4,
    locality: v.locality,
    town: v.town,
    county: v.county,
    postcode: v.postcode,
  };
}

type AddressLookupResponse = {
  data: LocationFormAddress[];
};

interface VehicleLocationProps {
  vehicle: IVehicle | null;
  variant?: "embedded" | "standalone";
}

export default function VehicleLocation({
  vehicle,
  variant = "standalone",
}: VehicleLocationProps) {
  const { t } = useTranslation("vehicles");
  const { patchVehicle } = useVehiclesMutations();
  const { getAddresses } = useUtilities();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle location details"));
  const { addNotification } = useNotification();
  const initialValues = useMemo(() => {
    return vehicle ? getFormValuesFromVehicle(vehicle) : emptyForm;
  }, [vehicle]);

  const handleSubmit = (data: LocationFormValues) => {
    if (!vehicle) return;

    patchVehicle.mutate(
      {
        vehicleId: vehicle.id,
        patch: {
          line1: data.line1,
          line2: data.line2,
          line3: data.line3,
          line4: data.line4,
          locality: data.locality,
          town: data.town,
          county: data.county,
          postcode: data.postcode,
        },
      },
      saveHandlers
    );
  };

  const handleGetAddresses = async (postcode: string) => {
    const response = await getAddresses.mutateAsync(postcode) as AddressLookupResponse;

    return response.data;
  };

  return (
    <LocationForm
      initialValues={initialValues}
      location={vehicle}
      onSubmit={handleSubmit}
      onGetAddresses={handleGetAddresses}
      onAddressLookupEmpty={() => {
        addNotification({
          variant: "error",
          primaryText: t("No addresses found"),
        });
      }}
      onAddressLookupError={() => {
        addNotification({
          variant: "error",
          primaryText: t("Could not get addresses"),
        });
      }}
      emptyMessage={t("No vehicle is linked to this lead.")}
      variant={variant}
      isSubmitting={patchVehicle.isLoading}
    >
      {vehicle && <VehicleCollectionQuestions vehicle={vehicle} />}
    </LocationForm>
  );
}
